/**
 * @fileoverview Account Group Registration Component for GoldERP
 * Redesigned to match StockGroupReg.jsx split-layout layout and tree structure exactly.
 * Displays a hierarchical Tree View of Primary -> Main -> Group -> Ledger relationships on the right.
 */

import React, { useRef, useEffect, useState } from "react";
import io from "socket.io-client";
import WindowHeader from "../../../Layout/WindowHeader/WindowHeader";
import ActionToolbar from "../../../NavBars/ActionToolbar";
import SherComboBox from "../../../ComboBoxes/SherComboBox";
import ssLogo from "../../../../assets/images/sslogo.png";
import axios from "axios";
import { useDbContext } from "../../../../context/DbContext";
import { useSelector } from "react-redux";
import { selectMenuPermissions } from "../../../../app/permissionsSlice";

const ChevronDown = () => (
  <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor"
    strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ChevronRight = () => (
  <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor"
    strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const HierarchyIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="6" cy="18" r="2.5" />
    <circle cx="6" cy="6" r="2.5" />
    <circle cx="18" cy="18" r="2.5" />
    <path d="M6 8.5v5" />
    <path d="M8.5 18h7" />
    <path d="M6 13.5a4.5 4.5 0 0 1 4.5 4.5" />
  </svg>
);

const TreeNode = ({ node, level = 0, onSelect, selectedName }) => {
  const [isOpen, setIsOpen] = useState(false);
  const isSelected = selectedName === node.name;
  const hasChildren = node.children && node.children.length > 0;

  const handleClick = () => {
    if (hasChildren) {
      setIsOpen((prev) => !prev);
    }
    if (onSelect) onSelect(node);
  };

  return (
    <div className="flex flex-col select-none">
      <div
        onClick={handleClick}
        className={`
          flex items-center py-[4px] cursor-pointer transition-colors duration-100 rounded-sm
          ${isSelected ? 'bg-[#d6e4f7]' : 'hover:bg-[#EAF0FC]'}
        `}
        style={{ paddingLeft: `${level * 14 + 4}px` }}
      >
        <span className="w-4 flex justify-center items-center shrink-0 text-[#213CA5]">
          {hasChildren ? (isOpen ? <ChevronDown /> : <ChevronRight />) : null}
        </span>
        <span
          className={`
            text-[12px] uppercase tracking-wide truncate leading-5 font-Inter
            ${node.level === 3
              ? 'text-[#213CA5] font-bold border-b border-dashed border-blue-200/50'
              : node.level === 4
              ? 'text-[#5A7DC0] font-medium pl-1'
              : 'text-slate-800 font-bold'
            }
          `}
        >
          {node.name}
        </span>
      </div>

      {isOpen && hasChildren && (
        <div>
          {node.children.map((child, index) => (
            <TreeNode
              key={`${child.name}-${index}`}
              node={child}
              level={level + 1}
              onSelect={onSelect}
              selectedName={selectedName}
            />
          ))}
        </div>
      )}
    </div>
  );
};

function GroupRegistration({ onClose, zIndex }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();
  const permissions = useSelector(selectMenuPermissions);

  // ── Refs ───────────────────────────────────────────────────────────────────
  const saveBtnRef = useRef(null);
  const nameInputRef = useRef(null);
  const underInputRef = useRef(null);

  // ── UI States ──────────────────────────────────────────────────────────────
  const [saveDisabled, setSaveDisabled] = useState(false);
  const [agentCode, setAgentCode] = useState("");
  const [ledgerHeadOptions, setLedgerHeadOptions] = useState([]);
  const [ledgerHeadsLoading, setLedgerHeadsLoading] = useState(false);
  const [treeData, setTreeData] = useState([]);

  // ── Form State ─────────────────────────────────────────────────────────────
  const [groupRegData, setGroupRegData] = useState({
    id: "",
    name: { label: "", value: null },
    under: { label: "", value: null },
  });

  const updateNavRefs = (refs) => {
    if (refs.saveButtonRef) {
      saveBtnRef.current = refs.saveButtonRef.current;
    } else if (refs.saveBtnRef) {
      saveBtnRef.current = refs.saveBtnRef.current;
    }
  };

  const processTreeData = (data) => {
    const processNode = (node, parent = null, level = 1) => {
      const uniqueKey = `${level}-${node.id}-${node.name}`;
      const newNode = {
        ...node,
        uniqueKey,
        level,
        parentId: parent ? parent.id : null,
        parentName: parent ? parent.name : null,
      };
      if (node.children && level < 3) {
        newNode.children = node.children.map(child => processNode(child, newNode, level + 1));
      } else {
        newNode.children = [];
      }
      return newNode;
    };
    return data.map(item => processNode(item, null, 1));
  };

  const fetchLedgerHeadsAndTree = async () => {
    if (!dbCode) return;
    setLedgerHeadsLoading(true);
    try {
      // 1. Fetch Ledger Heads (parent options)
      const resHeads = await axios.get(`${apiBaseUrl}/common/fetchledgerheads/${dbCode}`);
      if (resHeads.data.success) {
        const options = resHeads.data.data.map((item) => ({
          label: item.lh_name,
          value: item.lh_id,
        }));
        setLedgerHeadOptions(options);
      }

      // 2. Fetch Account Tree structure
      const resTree = await axios.get(`${apiBaseUrl}/accountregistration/getaccounttree/${dbCode}`);
      if (resTree.data) {
        setTreeData(processTreeData(resTree.data));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLedgerHeadsLoading(false);
    }
  };

  useEffect(() => {
    fetchLedgerHeadsAndTree();

    const socket = io(apiBaseUrl);
    socket.on("GroupUpdated", fetchLedgerHeadsAndTree);

    return () => {
      socket.off("GroupUpdated", fetchLedgerHeadsAndTree);
      socket.disconnect();
    };
  }, [dbCode, apiBaseUrl]);

  useEffect(() => {
    setTimeout(() => {
      nameInputRef.current?.focus?.();
    }, 150);
  }, []);

  useEffect(() => {
    const storedAgentCode = sessionStorage.getItem("agentCode");
    if (storedAgentCode) setAgentCode(storedAgentCode);
  }, []);

  const handleClear = () => {
    setGroupRegData({
      id: "",
      name: { label: "", value: null },
      under: { label: "", value: null },
    });
    setSaveDisabled(false);
    fetchLedgerHeadsAndTree();
    setTimeout(() => {
      nameInputRef.current?.focus?.();
    }, 100);
  };

  const handleSelectRecord = (node) => {
    // We only load groups (level 3 Ledger Heads) into the form fields
    if (node.level === 3) {
      setGroupRegData({
        id: String(node.id),
        name: { label: node.name || "", value: node.id },
        under: { label: node.parentName || "", value: node.parentId },
      });
    }
  };

  const handleKeyDown = (event, nextInputRef) => {
    if (event.key === "Enter" || event.key === "Return") {
      event.preventDefault();
      nextInputRef?.current?.focus?.();
    }
  };

  const emitDataUpdate = () => {
    const socket = io(apiBaseUrl);
    socket.emit("GroupUpdated");
    socket.emit("dataUpdated");
    socket.disconnect();
  };

  // ── CRUD Handlers ──────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!groupRegData.name.label.trim()) {
      alert("Validation Error: Please enter a Group Name.");
      nameInputRef.current?.focus?.();
      return;
    }
    if (!groupRegData.under.value) {
      alert("Validation Error: Please select a parent group (Under).");
      underInputRef.current?.focus?.();
      return;
    }

    setSaveDisabled(true);
    try {
      const payload = {
        name: groupRegData.name.label,
        underId: groupRegData.under.value,
        userId: agentCode || 1,
      };

      const response = await axios.post(
        `${apiBaseUrl}/accountregistration/insertGroup/${dbCode}`,
        payload
      );

      if (response.data.success) {
        alert("Group registered successfully!");
        handleClear();
        emitDataUpdate();
      } else {
        alert(response.data.message || "Failed to register group.");
        setSaveDisabled(false);
      }
    } catch (error) {
      console.error("Error saving group:", error);
      alert("Failed to save group.");
      setSaveDisabled(false);
    }
  };

  const handleEdit = async () => {
    if (!groupRegData.name.value) {
      alert("Validation Error: Please select a group to edit first.");
      return;
    }
    if (!groupRegData.name.label.trim()) {
      alert("Validation Error: Please enter a Group Name.");
      nameInputRef.current?.focus?.();
      return;
    }
    if (!groupRegData.under.value) {
      alert("Validation Error: Please select a parent group (Under).");
      underInputRef.current?.focus?.();
      return;
    }

    try {
      const payload = {
        name: groupRegData.name.label,
        nameId: groupRegData.name.value,
        underId: groupRegData.under.value,
        userId: agentCode || 1,
      };

      const response = await axios.put(
        `${apiBaseUrl}/accountregistration/updateGroupReg/${dbCode}`,
        payload
      );

      if (response.data.success) {
        alert("Group updated successfully!");
        handleClear();
        emitDataUpdate();
      } else {
        alert(response.data.message || "Failed to update group.");
      }
    } catch (error) {
      console.error("Error updating group:", error);
      alert("Failed to update group.");
    }
  };

  const handleDelete = async () => {
    if (!groupRegData.name.value) {
      alert("Validation Error: Please select a group to delete first.");
      return;
    }

    const confirmed = window.confirm(
      `Do you want to delete the group "${groupRegData.name.label}"?`
    );
    if (!confirmed) return;

    try {
      const response = await axios.delete(
        `${apiBaseUrl}/accountregistration/deleteGroupReg/${dbCode}`,
        {
          params: {
            name: groupRegData.name.label,
            nameid: groupRegData.name.value,
            userid: agentCode || 1,
          },
        }
      );

      if (response.data.success) {
        alert("Group deleted successfully!");
        handleClear();
        emitDataUpdate();
      } else {
        alert(response.data.message || "Cannot delete group.");
      }
    } catch (error) {
      console.error("Error deleting group:", error);
      alert("Failed to delete group.");
    }
  };
  
  const handleFind = async () => {
    if (!groupRegData.name.value) {
      alert("Please select a valid Name before searching.");
      nameInputRef.current?.focus?.();
      return;
    }

    try {
      const response = await axios.get(
        `${apiBaseUrl}/accountregistration/findGroupReg/${dbCode}`,
        { params: { name: groupRegData.name.label } }
      );

      if (response.data && response.data.length > 0) {
        const foundGroup = response.data[0];
        console.log("Found Group:", foundGroup);

        const parentId = !foundGroup.lh_Code ? foundGroup.Mlh_id : foundGroup.lh_Code;
        const parentOpt = ledgerHeadOptions.find((opt) => opt.value === parentId);
        const parentLabel = parentOpt ? parentOpt.label : "";

        setGroupRegData({
          id: foundGroup.lh_id.toString(),
          name: { label: foundGroup.lh_name, value: foundGroup.lh_id },
          under: { label: parentLabel, value: parentId },
        });

        setSaveDisabled(true);
      } else {
        alert(`No group found with name "${groupRegData.name.label}".`);
      }
    } catch (error) {
      console.error("Error finding group:", error);
      alert("Error: Failed to search for group.");
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-3 font-Inter select-none"
      style={{ zIndex: zIndex || 101, fontFamily: "Inter, sans-serif" }}
    >
      <div className="bg-white font-normal w-[720px] max-w-[95vw] h-[520px] max-h-[85vh] rounded-xl shadow-2xl flex flex-col overflow-hidden border border-slate-200">
        {/* Header */}
        <WindowHeader
          title="Account Group Registration"
          logo={ssLogo}
          onClose={onClose}
          closeBtnClassName="w-6 h-6 border-none rounded-[5px] mr-2"
        />

        {/* Toolbar */}
        <div className="w-full flex-shrink-0">
          <ActionToolbar
            width="100%"
            height="auto"
            bgColor="#EAF0FC"
            size="small"
            iconSize={16}
            showSave={true}
            showPrint={false}
            showClear={true}
            showFind={true}
            showEdit={true}
            showDelete={true}
            showExit={true}
            showNavigation={false}
            saveDisabled={!!groupRegData.name.value || saveDisabled}
            editDisabled={!groupRegData.name.value}
            deleteDisabled={!groupRegData.name.value}
            findDisabled={false}
            dataFound={!!groupRegData.name.value}
            onSave={handleSubmit}
            onClear={handleClear}
            onFind={handleFind}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onExit={onClose}
            onRefsUpdate={updateNavRefs}
          />
        </div>

        {/* Main Content Body */}
        <div className="w-full flex-1 p-3.5 bg-slate-50 flex gap-4 items-stretch min-h-[300px] overflow-hidden">
          
          {/* LEFT COLUMN: Add / Edit Form */}
          <div className="flex-1 bg-white p-3.5 rounded-lg border border-slate-200 shadow-sm flex flex-col gap-3 overflow-visible">
            <h3 className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider m-0 font-Inter">
              ADD / EDIT ACCOUNT GROUP
            </h3>

            {/* Group ID — Read Only */}
            <div className="flex flex-col gap-1">
              <label className="text-[11.5px] font-bold text-slate-700 font-Inter">
                Group ID
              </label>
              <input
                type="text"
                readOnly
                placeholder="Auto"
                value={groupRegData.id}
                className="w-full h-8 px-2.5 text-xs font-bold border border-slate-300 rounded-md outline-none bg-[#fef9c3] text-[#854d0e] border-[#fef08a]"
              />
            </div>

            {/* Name */}
            <div className="flex flex-col gap-1">
              <label className="text-[11.5px] font-bold text-slate-700 font-Inter">
                Group Name <span className="text-red-500">*</span>
              </label>
              <SherComboBox
                comboRef={nameInputRef}
                options={ledgerHeadOptions}
                loading={ledgerHeadsLoading}
                value={groupRegData.name.label}
                onSelect={(opt) => {
                  setGroupRegData((prev) => ({
                    ...prev,
                    name: { label: opt.label, value: opt.value },
                  }));
                }}
                onInputChange={(text) => {
                  setGroupRegData((prev) => ({
                    ...prev,
                    name: { label: text, value: text === "" ? null : prev.name.value },
                  }));
                }}
                onFreeInput={(text) => {
                  setGroupRegData((prev) => ({
                    ...prev,
                    name: { ...prev.name, label: text },
                  }));
                }}
                placeholder="Enter or select group name..."
                showArrow={true}
                clearable={false}
                inputWidth="100%"
                inputHeight="32px"
                inputClassName="border border-slate-300 rounded-md focus:border-blue-600 shadow-2xs font-Inter"
                inputStyle={{ fontSize: "12px", paddingLeft: "8px", fontFamily: "Inter, sans-serif" }}
                onKeyDown={(e) => handleKeyDown(e, underInputRef)}
              />
            </div>

            {/* Under */}
            <div className="flex flex-col gap-1">
              <label className="text-[11.5px] font-bold text-slate-700 font-Inter">
                Under (Parent Group) <span className="text-red-500">*</span>
              </label>
              <SherComboBox
                comboRef={underInputRef}
                options={ledgerHeadOptions}
                loading={ledgerHeadsLoading}
                value={groupRegData.under.label || groupRegData.under.value}
                onSelect={(opt) => {
                  setGroupRegData((prev) => ({
                    ...prev,
                    under: { label: opt.label, value: opt.value },
                  }));
                }}
                onInputChange={(text) => {
                  setGroupRegData((prev) => ({
                    ...prev,
                    under: { label: text, value: text === "" ? null : prev.under.value },
                  }));
                }}
                onFreeInput={(text) => {
                  setGroupRegData((prev) => ({
                    ...prev,
                    under: { ...prev.under, label: text },
                  }));
                }}
                placeholder="Select parent group..."
                showArrow={true}
                clearable={false}
                inputWidth="100%"
                inputHeight="32px"
                inputClassName="border border-slate-300 rounded-md focus:border-blue-600 shadow-2xs font-Inter"
                inputStyle={{ fontSize: "12px", paddingLeft: "8px", fontFamily: "Inter, sans-serif" }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === "Return") {
                    e.preventDefault();
                    groupRegData.name.value ? handleEdit() : handleSubmit();
                  }
                }}
              />
            </div>

            {/* Submit / Update Button */}
            <button
              type="button"
              ref={saveBtnRef}
              onClick={groupRegData.name.value ? handleEdit : handleSubmit}
              disabled={saveDisabled}
              className="w-full mt-2 py-1.5 px-3 bg-[#1a3abf] hover:bg-[#142e99] active:bg-[#0f2378] text-white font-bold text-xs rounded-lg shadow-sm transition-all cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50 font-Inter"
            >
              <span>{groupRegData.name.value ? "Update Group" : "Save Group"}</span>
            </button>
          </div>

          {/* RIGHT COLUMN: Account Tree Hierarchy */}
          <div className="w-[280px] shrink-0 bg-[#EAF0FC] border border-[#D1E0F5] rounded-lg overflow-hidden flex flex-col">
            <div className="flex-shrink-0 bg-[#213CA5] text-white flex items-center px-3 py-2">
              <HierarchyIcon size={14} />
              <span className="ml-2 text-[12px] font-semibold tracking-wide font-Inter">Account Structure</span>
            </div>

            <div className="flex-1 overflow-y-auto p-3 tree-scrollbar bg-white">
              {treeData.length === 0 ? (
                <div className="py-8 text-center text-xs text-slate-400 italic font-Inter">
                  No accounts registered
                </div>
              ) : (
                treeData.map((node, index) => (
                  <TreeNode
                    key={`${node.name}-${index}`}
                    node={node}
                    level={0}
                    onSelect={handleSelectRecord}
                    selectedName={groupRegData.name.label}
                  />
                ))
              )}
            </div>
          </div>

        </div>
      </div>

      <style>{`
        .tree-scrollbar::-webkit-scrollbar { width: 5px; }
        .tree-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; }
        .tree-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .tree-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>
    </div>
  );
}

export default GroupRegistration;
